import { DiscountedItem, DiscountedItemCategory } from 'modules/campaigns/hooks/useDiscountedItems';

const matchesSearchTerm = (text: string, searchTerm: string) =>
  text.toLowerCase().includes(searchTerm.trim().toLowerCase());
export const filterItemsBySearchTerm = (items: DiscountedItem[], searchTerm: string) =>
  !searchTerm ? items : items.filter(item => matchesSearchTerm(item.name, searchTerm));

export type DiscountedItemCategoryWithMatches = {
  id: string;
  name: string;
  itemIds: string[];
  childCategories: DiscountedItemCategoryWithMatches[];
  /** Original item ids when filtering has been done */
  allItemIds?: string[];
  /** Original child categories ids when filtering has been done */
  allChildCategories?: DiscountedItemCategory[];
};
export const findCategoryItemsBySearchTerm = (
  discountedItems: DiscountedItem[],
  discountedItemCategories: DiscountedItemCategory[],
  searchTerm: string,
  additionalShownCategoryIds: string[] = [],
) => {
  if (!searchTerm) {
    return discountedItemCategories;
  }

  const itemMatches = filterItemsBySearchTerm(discountedItems, searchTerm);

  const filterCategoriesBySearchTerm = (categories: DiscountedItemCategory[], includeAllChildren?: boolean) =>
    categories.reduce<DiscountedItemCategoryWithMatches[]>((filteredCategories, category) => {
      const includeWholeCategory = includeAllChildren || additionalShownCategoryIds.includes(category.id);

      // Find items that match inside that category
      const matchingItemIds = includeWholeCategory
        ? category.itemIds
        : category.itemIds.filter(itemId => itemMatches.some(item => item.id === itemId));

      // Go through child categories to find matches
      const matchingChildCategories = filterCategoriesBySearchTerm(category.childCategories, includeWholeCategory);

      if (matchingItemIds.length || matchingChildCategories.length) {
        filteredCategories.push({
          ...category,
          itemIds: matchingItemIds,
          childCategories: matchingChildCategories,
          allItemIds: category.itemIds,
          allChildCategories: category.childCategories,
        });
      }

      return filteredCategories;
    }, []);

  return filterCategoriesBySearchTerm(discountedItemCategories);
};

const isWholeCategoryMatchingRecursive = (category: DiscountedItemCategoryWithMatches): boolean =>
  // No filtering done or everything matches the filter
  (!category.allItemIds || category.allItemIds?.length === category.itemIds.length) &&
  (!category.allChildCategories || category.allChildCategories?.length === category.childCategories.length) &&
  category.childCategories.every(isWholeCategoryMatchingRecursive);

export const searchResultToIdsForSelectAll = (categoriesWithMatches: DiscountedItemCategoryWithMatches[]) =>
  categoriesWithMatches.reduce<{ itemIds: string[]; categoryIds: string[] }>(
    (result, category) => {
      // Check if whole category is included, and if yes, add only the top-most category id
      const isWholeCategoryIncluded = isWholeCategoryMatchingRecursive(category);

      if (isWholeCategoryIncluded) {
        result.categoryIds.push(category.id);
      } else {
        result.itemIds.push(...category.itemIds);

        if (category.childCategories.length) {
          const childResults = searchResultToIdsForSelectAll(category.childCategories);

          result.categoryIds.push(...childResults.categoryIds);
          result.itemIds.push(...childResults.itemIds);
        }
      }

      return result;
    },
    { itemIds: [], categoryIds: [] },
  );

type SelectionByCategory = {
  selectedCategories: DiscountedItemCategory[];
  selectedCategoriesCount: number;
  selectedOtherItemIds: string[];
  totalSelectedItemsCount: number;
};
export const getSelectionByCategory = (
  discountedItemCategories: DiscountedItemCategory[],
  selectedItemIds: string[],
  selectedCategoryIds: string[],
) => {
  const getSelectionByCategoryRecursive = (
    categories: DiscountedItemCategory[],
    includeAll: boolean,
  ): SelectionByCategory =>
    categories.reduce<SelectionByCategory>(
      (result, category) => {
        const includeCategory = includeAll || selectedCategoryIds.includes(category.id);

        const childResults = getSelectionByCategoryRecursive(category.childCategories, includeCategory);

        // Check if all the items in the category are included, and if yes, include the category
        const selectedCategoryItemIds = includeCategory
          ? category.itemIds
          : category.itemIds.filter(itemId => selectedItemIds.includes(itemId));
        const allItemsSelected =
          !!category.itemIds.length && category.itemIds.length === selectedCategoryItemIds.length;

        const totalSelectedItemsCount =
          result.totalSelectedItemsCount + selectedCategoryItemIds.length + childResults.totalSelectedItemsCount;

        // Check if the category should be displayed as selected
        if (includeCategory || allItemsSelected || childResults.selectedCategories.length) {
          const visibleCategory = {
            ...category,
            itemIds: selectedCategoryItemIds,
            childCategories: childResults.selectedCategories,
          };

          // If the parent is selected, do not add child categories to the count
          const addToCategoriesCount = !includeAll && (includeCategory || allItemsSelected) ? 1 : 0;
          const childCategoriesCount = includeAll ? 0 : childResults.selectedCategoriesCount;

          return {
            selectedCategories: [...result.selectedCategories, visibleCategory],
            selectedCategoriesCount: result.selectedCategoriesCount + addToCategoriesCount + childCategoriesCount,
            selectedOtherItemIds: [...result.selectedOtherItemIds, ...childResults.selectedOtherItemIds],
            totalSelectedItemsCount,
          };
        }

        // If the whole category was not selected, add the selected items from the category to other selected items
        return {
          ...result,
          selectedOtherItemIds: [
            ...result.selectedOtherItemIds,
            ...selectedCategoryItemIds,
            ...childResults.selectedOtherItemIds,
          ],
          totalSelectedItemsCount,
        };
      },
      { selectedCategories: [], selectedCategoriesCount: 0, selectedOtherItemIds: [], totalSelectedItemsCount: 0 },
    );

  return getSelectionByCategoryRecursive(discountedItemCategories, false);
};

/** Helper to calculate which categories/items to display in a selection summary */
export const getVisibleCategoriesWithItems = (
  selectedCategories: DiscountedItemCategory[],
  selectedOtherItemIds: string[],
  discountedItems: DiscountedItem[],
  selectedItemIds: string[],
  selectedCategoryIds: string[],
  maxItemsCount: number,
) => {
  // Flatten categories for displaying, preserving the parent category names
  const getCategoriesFlat = (
    categories: DiscountedItemCategory[],
    parentCategoryNames: string[],
  ): (DiscountedItemCategory & { parentCategoryNames: string[] })[] =>
    categories.flatMap(category => {
      if (selectedCategoryIds.includes(category.id)) {
        // Display all child items directly under a selected category
        const getCategoryItemIdsRecursive = (category: DiscountedItemCategory): string[] => [
          ...category.itemIds,
          ...category.childCategories.flatMap(getCategoryItemIdsRecursive),
        ];
        return [{ ...category, parentCategoryNames, itemIds: getCategoryItemIdsRecursive(category) }];
      }
      const childResults = getCategoriesFlat(category.childCategories, [...parentCategoryNames, category.name]);
      if (category.itemIds.length) {
        // Display a category only if it contains selected items
        return [{ ...category, parentCategoryNames }, ...childResults];
      }
      return childResults;
    });
  const flatCategories = getCategoriesFlat(selectedCategories, []);
  const cumulativeItemCounts = flatCategories.reduce<number[]>(
    (counts, category, index) => [...counts, (index === 0 ? 0 : counts[index - 1]) + category.itemIds.length],
    [],
  );
  // Find the first category that can't be fully displayed due to too many items
  const firstLimitedCategoryIndex = cumulativeItemCounts.findIndex(count => count > maxItemsCount);
  // Figure out how many items from that category should be included
  const limitedCategoryItemCount =
    firstLimitedCategoryIndex <= 0
      ? maxItemsCount
      : maxItemsCount - cumulativeItemCounts[firstLimitedCategoryIndex - 1];
  // Always display at least one category if categories are selected
  const limitedCategories =
    firstLimitedCategoryIndex === -1
      ? flatCategories
      : flatCategories.slice(
          0,
          limitedCategoryItemCount === 0 ? firstLimitedCategoryIndex : firstLimitedCategoryIndex + 1,
        );

  const visibleCategoriesWithItems = limitedCategories.map((category, index) => ({
    ...category,
    visibleItems: (index === limitedCategories.length - 1 && !!limitedCategoryItemCount
      ? category.itemIds.slice(0, limitedCategoryItemCount)
      : category.itemIds
    ).map(itemId => discountedItems.find(item => item.id === itemId)),
  }));
  const visibleCategoryItemCount =
    limitedCategories.length === 0 ? 0 : cumulativeItemCounts[limitedCategories.length - 1];
  const maxOtherItemsCount = Math.max(0, maxItemsCount - visibleCategoryItemCount);
  const visibleOtherItems = selectedOtherItemIds
    .slice(0, maxOtherItemsCount)
    .map(itemId => discountedItems.find(item => item.id === itemId));

  const hasMore =
    // There are more categories than are displayed
    limitedCategories.length < flatCategories.length ||
    // There are more other items than are displayed
    visibleOtherItems.length < selectedOtherItemIds.length ||
    // There is a category that is only partially displayed
    (firstLimitedCategoryIndex !== -1 &&
      limitedCategoryItemCount < limitedCategories[firstLimitedCategoryIndex].itemIds.length);
  const hasMissingItems =
    selectedCategoryIds.some(id => !flatCategories.some(category => category.id === id)) ||
    selectedItemIds.some(id => !discountedItems.some(item => item.id === id));

  return { visibleCategoriesWithItems, visibleOtherItems, hasMore, hasMissingItems };
};

const getCategoryIdsRecursive = (category: DiscountedItemCategory): { categoryIds: string[]; itemIds: string[] } => {
  const childIds = category.childCategories.map(getCategoryIdsRecursive);
  const childCategoryIds = childIds.flatMap(({ categoryIds }) => categoryIds);
  const childItemIds = childIds.flatMap(({ itemIds }) => itemIds);
  const categoryIds = [category.id].concat(childCategoryIds);
  const itemIds = category.itemIds.concat(childItemIds);
  return { categoryIds, itemIds };
};

/** Helper for handling the change of selected items vs categories for a category-level checkbox */
export const handleCategorySelectionChange = (
  selectedItemIds: string[],
  selectedCategoryIds: string[],
  category: DiscountedItemCategory,
  /** Provide if the category is displayed as selected due to its parent being selected */
  selectedParentCategory: DiscountedItemCategory | undefined,
  newSelectedValue: boolean,
) => {
  // Filter away any items and child categories that belong to the category to not have duplicates
  const { categoryIds, itemIds } = getCategoryIdsRecursive(category);
  const newSelectedItemIds = selectedItemIds.filter(id => !itemIds.includes(id));
  const filteredCategoryIds = selectedCategoryIds.filter(id => !categoryIds.includes(id));

  // If the click was to select the whole category, add it to the selection
  if (newSelectedValue) {
    const newSelectedCategoryIds = filteredCategoryIds.concat(category.id);
    return { itemIds: newSelectedItemIds, categoryIds: newSelectedCategoryIds };
  }

  // For remove selection, figure out if the category itself or its parent was selected
  if (selectedParentCategory) {
    // If a parent was selected, remove it from the selection and add the other of its children
    // This logic currently only works for two levels of categories, and not a deeper tree
    const neighborCategoryIds = selectedParentCategory.childCategories
      .map(childCategory => childCategory.id)
      .filter(id => id !== category.id);
    const newSelectedCategoryIds = filteredCategoryIds
      .filter(id => id !== selectedParentCategory.id)
      .concat(neighborCategoryIds);

    return { itemIds: newSelectedItemIds, categoryIds: newSelectedCategoryIds };
  }

  return { itemIds: newSelectedItemIds, categoryIds: filteredCategoryIds };
};

/** Helper for handling the change of selected items vs categories for a category-level toggle of "Include all items" */
export const handleCategoryVsAllItemsChange = (
  selectedItemIds: string[],
  selectedCategoryIds: string[],
  category: DiscountedItemCategory,
  /** Provide if the item is displayed as include all items due to its parent having the property */
  selectedParentCategory: DiscountedItemCategory | undefined,
  newIncludeValue: boolean,
) => {
  if (newIncludeValue) {
    // Filter away any items and child categories that belong to the category to not have duplicates
    const { categoryIds, itemIds } = getCategoryIdsRecursive(category);
    const newSelectedItemIds = selectedItemIds.filter(id => !itemIds.includes(id));
    const filteredCategoryIds = selectedCategoryIds.filter(id => !categoryIds.includes(id));

    // Add the category to selected categories
    const newSelectedCategoryIds = filteredCategoryIds.concat(category.id);

    return { itemIds: newSelectedItemIds, categoryIds: newSelectedCategoryIds };
  }

  // Remove the toggled category and all its child categories from the selection
  const { categoryIds, itemIds } = getCategoryIdsRecursive(category);
  const filteredCategoryIds = selectedCategoryIds.filter(id => !categoryIds.includes(id));

  if (selectedParentCategory) {
    // If a parent was selected, remove it from the selection too and add the other of its children
    // This logic currently only works for two levels of categories, and not a deeper tree
    const neighborCategoryIds = selectedParentCategory.childCategories
      .map(childCategory => childCategory.id)
      .filter(id => id !== category.id);
    const newSelectedCategoryIds = filteredCategoryIds
      .filter(id => id !== selectedParentCategory.id)
      .concat(neighborCategoryIds);
    const newSelectedItemIds = selectedItemIds.concat(category.itemIds);

    return { itemIds: newSelectedItemIds, categoryIds: newSelectedCategoryIds };
  }

  // If a parent was selected, remove it from the selection and all of its items

  // Add all the items in the category and it's child categories
  const newSelectedItemIds = selectedItemIds.concat(itemIds);

  return { itemIds: newSelectedItemIds, categoryIds: filteredCategoryIds };
};

/** Helper for handling the change of selected items vs categories for a item-level checkbox */
export const handleItemSelectionChange = (
  selectedItemIds: string[],
  selectedCategoryIds: string[],
  itemId: string,
  /** Provide if the item is displayed as selected due to its parent being selected */
  selectedParentCategory: DiscountedItemCategory | undefined,
  newSelectedValue: boolean,
) => {
  if (newSelectedValue) {
    // Handle adding the item to selected items
    const newSelectedItemIds = selectedItemIds.concat(itemId);
    // Selected categories do not change
    return { itemIds: newSelectedItemIds, categoryIds: selectedCategoryIds };
  }

  // Remove selection: different actions if a category or the item itself was selected
  if (selectedParentCategory) {
    // If a parent was selected, remove it from the selection and add the other of its children
    const filteredCategoryIds = selectedCategoryIds.filter(id => id !== selectedParentCategory.id);

    if (selectedParentCategory.itemIds.includes(itemId)) {
      const neighborItemIds = selectedParentCategory.itemIds.filter(id => id !== itemId);
      const newSelectedItemIds = selectedItemIds.concat(neighborItemIds);

      return { itemIds: newSelectedItemIds, categoryIds: filteredCategoryIds };
    }

    // Handle case when a higher-level category was the one selected
    // This logic currently only works for two levels of categories, and not a deeper tree
    const directParentCategory = selectedParentCategory.childCategories.find(childCategory =>
      childCategory.itemIds.includes(itemId),
    );

    const neighborCategoryIds = selectedParentCategory.childCategories
      .map(childCategory => childCategory.id)
      .filter(id => id !== directParentCategory?.id);

    const newSelectedCategoryIds = filteredCategoryIds.concat(neighborCategoryIds);

    const neighborItemIds = directParentCategory?.itemIds.filter(id => id !== itemId) ?? [];
    const newSelectedItemIds = selectedItemIds.concat(neighborItemIds);

    return { itemIds: newSelectedItemIds, categoryIds: newSelectedCategoryIds };
  }

  // Remove the item from selected item ids, preserve selected categories
  const newSelectedItemIds = selectedItemIds.filter(id => id !== itemId);
  // Selected categories do not change
  return { itemIds: newSelectedItemIds, categoryIds: selectedCategoryIds };
};
